import type { MusicAccessFlowAccess } from "./schema";
import { MUSIC_ACCESS_FLOW_COPY } from "./recipe";

export type MusicAccessPrimaryAction =
  | { kind: "external"; urls: string[] }
  | { kind: "owned" }
  // For the artist's fan club's members, which the viewer is not: nothing
  // to unlock or buy.
  | { kind: "members-only" }
  | { kind: "unlock-free" }
  | { kind: "checkout"; screen: "checkout" };

export type MusicAccessDetailsModel = {
  accessId: string;
  artistId: string;
  artistName: string;
  artistImageUrl: string | null;
  title: string;
  heroImageUrl: string | null;
  songId: string;
  song: MusicAccessFlowAccess["songs"][number];
  exclusiveLabel: "Exclusive Song" | "Exclusive DJ Set";
  orderItemLabel: "Exclusive Song" | "Exclusive DJ Set";
  durationSeconds: number;
  effectivePrice: number;
  priceMin: number | null;
  priceMax: number | null;
  priceCurrency: string;
  unlockCode?: string;
  unlocked: boolean;
  isExternal: boolean;
  /** Whose fan club members-only music is for (its owner artist), while the
   *  viewer is not a member; null otherwise. */
  clubArtistName: string | null;
  shouldShowPrice: boolean;
  primaryAction: MusicAccessPrimaryAction;
};

export type MusicAccessCheckoutHeaderModel = {
  accessId: string;
  title: string;
  artistName: string;
  heroImageUrl: string | null;
  durationSeconds: number;
  itemLabel: "Exclusive Song" | "Exclusive DJ Set";
};

export function isExternalMusicAccess(
  access: Pick<MusicAccessFlowAccess, "extLinks" | "extSource">,
): boolean {
  if (access.extSource === "bandcamp-ingested") return false;
  return Array.isArray(access.extLinks) && access.extLinks.length > 0;
}

export function isMusicAccessDjSet(input: {
  access: Pick<MusicAccessFlowAccess, "songs">;
  catalogSongIsSetList?: boolean;
}): boolean {
  return Boolean(
    input.access.songs.at(0)?.isSetList || input.catalogSongIsSetList,
  );
}

export function getMusicAccessPrimaryAction(input: {
  access: MusicAccessFlowAccess;
  unlockCode?: string;
}): MusicAccessPrimaryAction {
  const { access, unlockCode } = input;
  if (isExternalMusicAccess(access)) {
    return { kind: "external", urls: access.extLinks ?? [] };
  }
  if (access.unlocked) return { kind: "owned" };
  if (access.membersOnly) return { kind: "members-only" };
  if (unlockCode || access.price === 0) return { kind: "unlock-free" };
  return { kind: "checkout", screen: "checkout" };
}

export function createMusicAccessDetailsModel(input: {
  access: MusicAccessFlowAccess;
  unlockCode?: string;
  catalogSongIsSetList?: boolean;
}): MusicAccessDetailsModel {
  const { access, unlockCode, catalogSongIsSetList } = input;
  const song = access.songs[0];
  const isDjSet = isMusicAccessDjSet({ access, catalogSongIsSetList });
  const exclusiveLabel = isDjSet
    ? MUSIC_ACCESS_FLOW_COPY.exclusiveDjSet
    : MUSIC_ACCESS_FLOW_COPY.exclusiveSong;
  const primaryAction = getMusicAccessPrimaryAction({ access, unlockCode });

  return {
    accessId: access.id,
    artistId: access.artistId,
    artistName: access.artistName,
    artistImageUrl: access.artistImage,
    title: song.title || access.title,
    heroImageUrl: song.albums.at(0)?.image ?? access.artistImage,
    songId: song.id,
    song,
    exclusiveLabel,
    orderItemLabel: exclusiveLabel,
    durationSeconds: song.duration,
    effectivePrice: unlockCode ? 0 : access.price,
    priceMin: access.priceMin,
    priceMax: access.priceMax,
    priceCurrency: access.priceCurrency,
    unlockCode,
    unlocked: access.unlocked,
    isExternal: primaryAction.kind === "external",
    clubArtistName:
      primaryAction.kind === "members-only"
        ? (access.clubArtistName ?? access.artistName)
        : null,
    // Members-only music has no price to pay.
    shouldShowPrice: !access.unlocked && !access.membersOnly,
    primaryAction,
  };
}

export function createMusicAccessCheckoutHeaderModel(input: {
  access: MusicAccessFlowAccess;
  catalogSongIsSetList?: boolean;
}): MusicAccessCheckoutHeaderModel {
  const details = createMusicAccessDetailsModel(input);
  return {
    accessId: details.accessId,
    title: details.title,
    artistName: details.artistName,
    heroImageUrl: details.heroImageUrl,
    durationSeconds: details.durationSeconds,
    itemLabel: details.orderItemLabel,
  };
}
